import React, { useState } from 'react';
import { HistoricoEquipamentoData, CreateHistoricoDto, RegistroHistorico } from '../types/historico';
import './FormularioHistorico.css';

const getLocalDateInput = () => {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - timezoneOffset).toISOString().split('T')[0];
};

const formatDatePtBr = (value: string) => {
  const datePart = value.includes('T') ? value.split('T')[0] : value;
  const [year, month, day] = datePart.split('-');

  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
};

interface FormularioHistoricoProps {
  historico?: HistoricoEquipamentoData;
  onSalvar: (data: any) => void;
  onCancelar: () => void;
  isEditing?: boolean;
}

export const FormularioHistorico: React.FC<FormularioHistoricoProps> = ({
  historico,
  onSalvar,
  onCancelar,
  isEditing = false
}) => {
  const [formData, setFormData] = useState<HistoricoEquipamentoData>(
    historico || {
      numeroSerie: '',
      modelo: '',
      registros: [],
      notas: ''
    }
  );

  // Rastrear IDs de registros originais (imutáveis)
  const [registrosOriginaisIds] = useState<Set<string>>(
    new Set((historico?.registros.map(r => r.id) || []).filter((id): id is string => !!id))
  );

  const [novoRegistro, setNovoRegistro] = useState<RegistroHistorico>({
    data: getLocalDateInput(),
    historico: '',
    assinatura: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegistroInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNovoRegistro(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const novoRegistroTemConteudo = () =>
    Boolean(novoRegistro.historico.trim() || novoRegistro.assinatura.trim());

  const novoRegistroEstaCompleto = () =>
    Boolean(novoRegistro.data && novoRegistro.historico.trim() && novoRegistro.assinatura.trim());

  const criarRegistroComId = (): RegistroHistorico => ({
    ...novoRegistro,
    historico: novoRegistro.historico.trim(),
    assinatura: novoRegistro.assinatura.trim(),
    id: `novo-${Date.now()}`
  });

  const limparNovoRegistro = () => {
    setNovoRegistro({
      data: getLocalDateInput(),
      historico: '',
      assinatura: ''
    });
  };

  const adicionarRegistro = () => {
    if (!novoRegistroEstaCompleto()) {
      alert('Preencha todos os campos do registro');
      return;
    }

    const registroComId = criarRegistroComId();

    setFormData(prev => ({
      ...prev,
      registros: [...prev.registros, registroComId]
    }));

    limparNovoRegistro();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.numeroSerie || !formData.modelo) {
      alert('Preencha os campos obrigatórios: Número de Série e Modelo');
      return;
    }

    const registrosParaSalvar = [...formData.registros];

    if (novoRegistroTemConteudo()) {
      if (!novoRegistroEstaCompleto()) {
        alert('Preencha todos os campos do registro');
        return;
      }

      registrosParaSalvar.push(criarRegistroComId());
    }

    if (registrosParaSalvar.length === 0) {
      alert('Adicione pelo menos um registro');
      return;
    }

    const dadosAtualizados = {
      ...formData,
      registros: registrosParaSalvar
    };

    if (isEditing) {
      onSalvar(dadosAtualizados);
    } else {
      const { id, createdAt, updatedAt, ...dados } = dadosAtualizados;
      onSalvar(dados as CreateHistoricoDto);
    }
  };

  return (
    <div className="formulario-container">
      <h1>{isEditing ? 'Editar Histórico' : 'Novo Histórico do Equipamento'}</h1>

      <form onSubmit={handleSubmit} className="formulario-historico">
        <div className="form-section">
          <h2>FOR-MAN-008 - Histórico do Equipamento</h2>

          <div className="form-group">
            <label>Número de Série *</label>
            <input
              type="text"
              name="numeroSerie"
              value={formData.numeroSerie}
              onChange={handleInputChange}
              placeholder="Ex: CSEX420ACM-0559"
              disabled={isEditing}
              required
            />
          </div>

          <div className="form-group">
            <label>Modelo *</label>
            <input
              type="text"
              name="modelo"
              value={formData.modelo}
              onChange={handleInputChange}
              placeholder="Ex: CSEX420ACM"
              disabled={isEditing}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Registros</h2>

          <div className="novo-registro">
            <h3>Adicionar Novo Registro</h3>

            <div className="form-group">
              <label>Data *</label>
              <input
                type="date"
                name="data"
                value={novoRegistro.data}
                onChange={handleRegistroInputChange}
              />
            </div>

            <div className="form-group">
              <label>Histórico *</label>
              <textarea
                name="historico"
                value={novoRegistro.historico}
                onChange={handleRegistroInputChange}
                placeholder="Descreva o histórico do equipamento..."
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Responsável *</label>
              <input
                type="text"
                name="assinatura"
                value={novoRegistro.assinatura}
                onChange={handleRegistroInputChange}
                placeholder="Nome do responsável"
              />
            </div>

            <button
              type="button"
              onClick={adicionarRegistro}
              className="btn-adicionar-registro"
            >
              + Adicionar Registro
            </button>
          </div>

          {formData.registros.length > 0 && (
            <div className="registros-list">
              <h3>Registros Adicionados ({formData.registros.length})</h3>
              <table className="registros-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Histórico</th>
                    <th>Responsável</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.registros.map((registro) => {
                    const ehOriginal = isEditing && registrosOriginaisIds.has(registro.id || '');
                    return (
                      <tr key={registro.id} style={{ backgroundColor: ehOriginal ? '#f0f0f0' : 'transparent' }}>
                        <td>{formatDatePtBr(registro.data)}</td>
                        <td>{registro.historico.substring(0, 50)}...</td>
                        <td>{registro.assinatura}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="form-section">
          <div className="form-group">
            <label>Notas (Observações)</label>
            <textarea
              name="notas"
              value={formData.notas || ''}
              onChange={handleInputChange}
              placeholder="Notas adicionais sobre o equipamento..."
              rows={4}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-salvar">
            {isEditing ? 'Atualizar' : 'Salvar'} Histórico
          </button>
          <button type="button" onClick={onCancelar} className="btn-cancelar">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};
